import type L from 'leaflet';
import { paletteSpecies } from './charts/palette';
import { servedStandSpeciesCode } from '@/types/generated/forest_models';

export const CADASTRAL_BOUNDARY_STYLE: L.PathOptions = {
  color: 'var(--fm-error)',
  weight: 2,
  dashArray: '6, 4',
  fillColor: 'transparent',
  fillOpacity: 0,
  opacity: 0.8,
};

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function volumePerHectare(props: Record<string, unknown>): number | null {
  const served = finiteNumber(props.volume_per_hectare);
  if (served !== null) return served;
  const total = finiteNumber(props.volume_m3);
  const area = finiteNumber(props.area_hectares);
  return total !== null && area !== null && area > 0
    ? total / area
    : null;
}

function speciesFill(props: Record<string, unknown>): string {
  const code = servedStandSpeciesCode(props);
  return code === null ? 'var(--fm-d-fg-2)' : paletteSpecies(code);
}

function volumeOpacity(volume: number | null): number {
  if (volume === null) return 0.15;
  if (volume <= 60) return 0.3;
  if (volume >= 300) return 0.8;
  return 0.3 + ((volume - 60) / 240) * 0.5;
}

/** Canonical forest semantics: species fill, volume opacity, maturity stroke. */
export function getForestStandPathOptions(
  props: Record<string, unknown>,
): L.PathOptions {
  if (props._dimmed === true) {
    return {
      color: 'var(--fm-d-fg-2)',
      weight: 1,
      opacity: 0.22,
      fillColor: 'var(--fm-d-fg-2)',
      fillOpacity: 0.1,
    };
  }
  const volume = volumePerHectare(props);
  const fillColor = volume === null ? 'var(--fm-d-fg-2)' : speciesFill(props);
  const mature = Boolean(props.cutting_maturity);
  return {
    color: mature ? 'var(--fm-map-red)' : fillColor,
    weight: mature ? 3 : 2,
    opacity: 0.8,
    fillColor,
    fillOpacity: volumeOpacity(volume),
  };
}

export function getForestStandStyle(
  feature?: GeoJSON.Feature | null,
): L.PathOptions {
  return getForestStandPathOptions(feature?.properties ?? {});
}
